import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PremiumCertificate from '@/components/certificates/PremiumCertificate';
import PremiumMarksheet from '@/components/certificates/PremiumMarksheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Award, FileText, Download, Printer, ArrowLeft, CheckCircle,
  Shield, QrCode, Loader2
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CertificateViewPage = () => {
  const { certificateId, attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const certificateRef = useRef(null);
  const marksheetRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [marksheet, setMarksheet] = useState(null);
  const [settings, setSettings] = useState(null);
  const [studentPhoto, setStudentPhoto] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('certificate');

  // Determine if this is a dynamic exam certificate
  const isDynamicCert = location.pathname.includes('/dynamic/') || attemptId;

  useEffect(() => {
    if (isDynamicCert && attemptId) {
      fetchDynamicCertificate();
    } else {
      fetchCertificateDetails();
    }
  }, [certificateId, attemptId]);

  const fetchDynamicCertificate = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Get the exam attempt details
      const attemptRes = await axios.get(`${API}/dynamic-exam/attempt/${attemptId}`, { headers });
      const attempt = attemptRes.data;
      
      if (attempt.result !== 'pass') {
        toast.error('Certificate not available - Exam not passed');
        navigate('/student/certificates');
        return;
      }
      
      // Get settings
      let certSettings = null;
      try {
        const settingsRes = await axios.get(`${API}/certificate/settings`, { headers });
        certSettings = settingsRes.data;
      } catch (e) {
        console.log('Using default settings');
      }
      
      // Get student profile for photo
      let studentPhotoUrl = null;
      try {
        const profileRes = await axios.get(`${API}/student/profile`, { headers });
        studentPhotoUrl = profileRes.data?.photo_url || profileRes.data?.profile_image;
      } catch (e) {
        console.log('Could not fetch student photo');
      }
      
      // Also try admission data for student details
      let studentDetails = {};
      try {
        const admissionsRes = await axios.get(`${API}/admissions/my-admissions`, { headers });
        const admission = admissionsRes.data?.find(a => 
          a.course_id === attempt.course_id || 
          a.course_name?.toLowerCase() === attempt.course_name?.toLowerCase()
        );
        if (admission) {
          studentDetails = {
            father_name: admission.father_name,
            date_of_birth: admission.dob,
            enrollment_number: admission.enrollment_number || admission.id?.substring(0, 10),
            district: admission.district,
            state: admission.state,
            center_name: admission.center_name || 'IQRA CAD INSTITUTE',
            photo_url: admission.photo_url || studentPhotoUrl
          };
        }
      } catch (e) {
        console.log('Could not fetch admission details');
      }
      
      // Create certificate object from attempt
      const certData = {
        id: `dynamic-${attempt.id}`,
        certificate_number: `IQRA/CAN/${attempt.id?.substring(0, 8)?.toUpperCase()}`,
        student_name: attempt.candidate_name,
        father_name: studentDetails.father_name,
        date_of_birth: studentDetails.date_of_birth,
        enrollment_number: studentDetails.enrollment_number || attempt.id?.substring(0, 10),
        course_name: attempt.course_name || attempt.course_id,
        certificate_type: attempt.training_mode || 'STT',
        grade: getGradeFromPercentage(attempt.percentage),
        percentage: attempt.percentage,
        date_of_issue: attempt.end_time,
        place_of_issue: studentDetails.state || certSettings?.default_place,
        district: studentDetails.district,
        state: studentDetails.state,
        center_name: studentDetails.center_name,
        duration: '180 Hrs',
        status: 'valid',
        verification_status: 'verified',
        photo_url: studentDetails.photo_url || studentPhotoUrl
      };
      
      // Create marksheet object
      const marksheetData = {
        marksheet_number: `IQRA/MS/${attempt.id?.substring(0, 8)?.toUpperCase()}`,
        student_name: attempt.candidate_name,
        course_name: attempt.course_name || attempt.course_id,
        subjects: [
          { 
            name: attempt.course_name || 'Examination', 
            theory_marks: attempt.marks_obtained || 0, 
            practical_marks: 0, 
            total: attempt.marks_obtained || 0, 
            max_marks: attempt.total_marks || 100 
          }
        ],
        total_marks: attempt.marks_obtained,
        max_marks: attempt.total_marks,
        percentage: attempt.percentage,
        grade: certData.grade,
        result: 'PASS'
      };
      
      setCertificate(certData);
      setMarksheet(marksheetData);
      setSettings(certSettings);
      setStudentPhoto(studentDetails.photo_url || studentPhotoUrl);
    } catch (error) {
      toast.error('Failed to load certificate');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchCertificateDetails = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/certificate/detail/${certificateId}`, { headers });
      
      setCertificate(res.data.certificate);
      setMarksheet(res.data.marksheet);
      setSettings(res.data.settings);
      
      // Try to get student photo from certificate data or profile
      if (res.data.certificate?.photo_url) {
        setStudentPhoto(res.data.certificate.photo_url);
      } else {
        try {
          const profileRes = await axios.get(`${API}/student/profile`, { headers });
          setStudentPhoto(profileRes.data?.photo_url || profileRes.data?.profile_image);
        } catch (e) {
          console.log('Could not fetch student photo');
        }
      }
    } catch (error) {
      toast.error('Failed to load certificate');
      console.error(error);
    }
    setLoading(false);
  };

  const getGradeFromPercentage = (percentage) => {
    if (!percentage) return 'N/A';
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 40) return 'D';
    return 'F';
  };

  const downloadAsPDF = async (type = 'certificate') => {
    setDownloading(true);
    try {
      const element = type === 'certificate' ? certificateRef.current : marksheetRef.current;
      if (!element) {
        toast.error('Document not ready');
        setDownloading(false);
        return;
      }

      // High resolution canvas for print-ready quality
      const canvas = await html2canvas(element, {
        scale: 3, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        imageTimeout: 15000
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      
      // A4 dimensions in mm
      const pdf = new jsPDF({
        orientation: type === 'certificate' ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: false // No compression for better quality
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate aspect ratio to fit properly
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      const finalWidth = imgWidth * ratio;
      const finalHeight = imgHeight * ratio;
      
      // Center the image
      const x = (pageWidth - finalWidth) / 2;
      const y = (pageHeight - finalHeight) / 2;
      
      pdf.addImage(imgData, 'PNG', x, y, finalWidth, finalHeight, undefined, 'FAST');
      
      const filename = type === 'certificate' 
        ? `Certificate_${certificate?.certificate_number?.replace(/\//g, '_')}.pdf`
        : `Marksheet_${marksheet?.marksheet_number?.replace(/\//g, '_')}.pdf`;
      
      pdf.save(filename);
      toast.success(`${type === 'certificate' ? 'Certificate' : 'Marksheet'} downloaded successfully!`);
    } catch (error) {
      toast.error('Download failed. Please try again.');
      console.error(error);
    }
    setDownloading(false);
  };

  const handlePrint = (type = 'certificate') => {
    const element = type === 'certificate' ? certificateRef.current : marksheetRef.current;
    if (!element) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${type === 'certificate' ? 'Certificate' : 'Marksheet'} - ${certificate?.student_name}</title>
          <style>
            body { margin: 0; padding: 0; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${element.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <Shield className="w-20 h-20 text-red-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Certificate Not Found</h1>
          <p className="text-slate-600 mb-6">
            The certificate you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Award className="w-7 h-7 text-yellow-500" />
              {certificate.certificate_type === 'RPL' ? 'RPL Certificate' : 'STT Certificate'}
            </h1>
            <p className="text-slate-600">{certificate.student_name} • {certificate.course_name}</p>
          </div>

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => handlePrint(activeTab)}
              disabled={downloading}
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button 
              onClick={() => downloadAsPDF(activeTab)}
              disabled={downloading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download PDF
            </Button>
          </div>
        </div>

        {/* Certificate Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <InfoCard 
            icon={<Award className="w-5 h-5 text-yellow-500" />}
            label="Certificate No"
            value={certificate.certificate_number}
          />
          <InfoCard 
            icon={<CheckCircle className="w-5 h-5 text-green-500" />}
            label="Grade"
            value={certificate.grade}
          />
          <InfoCard 
            icon={<FileText className="w-5 h-5 text-blue-500" />}
            label="Percentage"
            value={`${certificate.percentage?.toFixed(1)}%`}
          />
          <InfoCard 
            icon={<Shield className="w-5 h-5 text-purple-500" />}
            label="Status"
            value={certificate.verification_status === 'verified' ? 'Verified' : certificate.status}
            valueClass={certificate.verification_status === 'verified' ? 'text-green-600' : ''}
          />
        </div>

        {/* Tabs for Certificate and Marksheet */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="certificate" className="flex items-center gap-2">
              <Award className="w-4 h-4" /> Certificate
            </TabsTrigger>
            {marksheet && (
              <TabsTrigger value="marksheet" className="flex items-center gap-2">
                <FileText className="w-4 h-4" /> Marksheet
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="certificate">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-500 to-amber-500 p-3 text-white text-center text-sm font-medium">
                Certificate for Skill Competency
              </div>
              <div className="p-4 overflow-x-auto">
                <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%' }}>
                  <PremiumCertificate 
                    ref={certificateRef}
                    certificate={certificate} 
                    settings={settings}
                    studentPhoto={studentPhoto}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {marksheet && (
            <TabsContent value="marksheet">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-3 text-white text-center text-sm font-medium">
                  Statement of Marks • Official Marksheet
                </div>
                <div className="p-4 overflow-x-auto">
                  <div style={{ transform: 'scale(0.6)', transformOrigin: 'top left', width: '166%' }}>
                    <PremiumMarksheet 
                      ref={marksheetRef}
                      marksheet={marksheet} 
                      certificate={certificate}
                      settings={settings}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Verification Info */}
        <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <QrCode className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-green-800 mb-1">Certificate Verification</h3>
              <p className="text-sm text-green-700 mb-2">
                This certificate can be verified by scanning the QR code or visiting:
              </p>
              <code className="text-xs bg-white px-3 py-1 rounded border border-green-200 text-green-800">
                {window.location.origin}/verify/{encodeURIComponent(certificate.certificate_number)}
              </code>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Info Card Component
const InfoCard = ({ icon, label, value, valueClass = '' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs text-slate-500">{label}</span>
    </div>
    <p className={`font-bold text-slate-800 truncate ${valueClass}`}>{value}</p>
  </div>
);

export default CertificateViewPage;
